import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FirmwareTrack } from '@bhops/shared';
import { firmwareEventsApi, type CreateFirmwareEventBody } from '@/api/firmware-events';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { ymdToIso } from '@/lib/format-date';

// Phase 6 cleanup → Phase 7 (plan §D-4 follow-up): operator UI for
// recording a new firmware-event row. The DB model + read endpoints
// shipped in Phase 6, but the create path was a TODO — this modal
// closes that gap. Submit calls POST /robots/:id/firmware-events
// (firmwareEventsApi.create) and invalidates both the latest-per-
// track and full-history query keys so FirmwareTab refreshes.

const TRACK_LABEL: Record<FirmwareTrack, string> = {
  BINARY: '바이너리',
  BUNDLE: '번들',
  TABLET_APP: '태블릿 앱',
};

interface Props {
  open: boolean;
  onClose: () => void;
  robotId: string;
}

function todayIso(): string {
  // YYYY-MM-DD in local timezone — the API accepts an ISO string and
  // we want the operator's "today" not UTC's.
  const now = new Date();
  const yyyy = now.getFullYear().toString().padStart(4, '0');
  const mm = (now.getMonth() + 1).toString().padStart(2, '0');
  const dd = now.getDate().toString().padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function emptyForm(): CreateFirmwareEventBody {
  return {
    track: FirmwareTrack.BINARY,
    version: '',
    appliedAt: todayIso(),
    note: '',
  };
}

export function FirmwareEventCreateModal({ open, onClose, robotId }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateFirmwareEventBody>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setError(null);
    }
  }, [open, robotId]);

  const create = useMutation({
    mutationFn: (body: CreateFirmwareEventBody) => firmwareEventsApi.create(robotId, body),
    onSuccess: (e) => {
      // Refresh both panels in FirmwareTab (latest cards + history table).
      qc.invalidateQueries({ queryKey: ['robot-firmware-latest', robotId] });
      qc.invalidateQueries({ queryKey: ['robot-firmware-history', robotId] });
      toast.success(
        `${TRACK_LABEL[e.track]} ${e.version} 적용이 기록되었습니다.`,
        '펌웨어 이벤트 추가',
      );
      setForm(emptyForm());
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(ev: FormEvent<HTMLFormElement>): void {
    ev.preventDefault();
    setError(null);
    const trimmedVersion = form.version.trim();
    if (!trimmedVersion) {
      setError('버전을 입력해 주세요.');
      return;
    }
    if (!form.appliedAt) {
      setError('적용일을 선택해 주세요.');
      return;
    }
    create.mutate({
      track: form.track,
      version: trimmedVersion,
      appliedAt: ymdToIso(form.appliedAt),
      note: form.note?.trim() || undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 펌웨어 이벤트"
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="firmware-event-create-form" disabled={create.isPending}>
            {create.isPending ? '저장 중…' : '기록'}
          </Button>
        </>
      }
    >
      <form id="firmware-event-create-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <Field label="트랙" htmlFor="fw-track" required>
          <Select
            id="fw-track"
            value={form.track}
            onChange={(e) => setForm((f) => ({ ...f, track: e.target.value as FirmwareTrack }))}
          >
            {Object.entries(TRACK_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="버전" htmlFor="fw-version" required>
          <Input
            id="fw-version"
            value={form.version}
            onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
            placeholder="예: 1.4.2"
          />
        </Field>

        <Field label="적용일" htmlFor="fw-applied" required>
          <Input
            id="fw-applied"
            type="date"
            value={form.appliedAt}
            onChange={(e) => setForm((f) => ({ ...f, appliedAt: e.target.value }))}
          />
        </Field>

        <Field label="메모" htmlFor="fw-note">
          <Textarea
            id="fw-note"
            value={form.note ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            rows={3}
            placeholder="변경 요약이나 적용 사유"
          />
        </Field>
      </form>
    </Modal>
  );
}
